// @ts-nocheck
import AspectRatioProvider from "@/components/modifiers/AspectRatioProvider";
import type { GeneratedCartItem } from "@/lib/centra/localAtomics";
import type {
  CartItemMaybeHooked,
  CartItemWithHooks,
} from "@/lib/hooks/useSelection/cart/useItems";
import clsx from "clsx";
import Image from "next/image";

import { useCartTabs } from "@/components/molecules/Wishlist/CartTabsContext";
import Link from "next/link";
import QuantitySelector from "../QuantitySelector/QuantitySelector";
import CartSizeSelector from "../CartSizeSelector/CartSizeSelector";
import { useProductSizes } from "@/lib/hooks/useSelection/cart/useProductSizes";
import styles from "./cart-item.module.css";

const CartItemPrice = ({
  discountedPrice,
  price,
}: {
  discountedPrice?: string;
  price?: string;
}) => {
  const isDiscounted = discountedPrice !== price;
  return (
    <div className={clsx(styles.discounted__wrapper)}>
      {isDiscounted && (
        <span className={styles.discounted__price}>{discountedPrice}</span>
      )}
      <p className={styles.regular__price}>{price}</p>
    </div>
  );
};

const getProductPageHref = (item: CartItemMaybeHooked | GeneratedCartItem) => {
  return "/product/" + item.uri;
};

const CartImage = ({
  item,
}: {
  item: CartItemMaybeHooked | GeneratedCartItem;
}) => {
  return (
    <Link className={styles.image} href={getProductPageHref(item)}>
      <AspectRatioProvider width={1} height={1.5}>
        <Image src={item?.media?.full?.href || ""} fill alt={""} />
      </AspectRatioProvider>
    </Link>
  );
};

const CartItemQuantity = ({
  item,
}: {
  item: CartItemWithHooks | undefined;
}) => {
  if (!item) return null;
  return (
    <div className={styles.quantity__wrapper}>
      <QuantitySelector
        quantity={item.quantity}
        increment={item.increment}
        decrement={item.decrement}
      />
    </div>
  );
};

const DeleteCartItem = ({ item }: { item: CartItemWithHooks | undefined }) => {
  if (!item) return null;
  return (
    <button onClick={item.remove}>
      <span className={styles.remove__button}>Delete</span>
    </button>
  );
};

const CartItem = ({
  item,
  price = true,
  quantity = false,
  simple = false,
  isPreorder,
}: {
  item: CartItemMaybeHooked | GeneratedCartItem;
  price?: boolean;
  quantity?: boolean;
  simple?: boolean;
  isPreorder?: boolean;
}) => {
  const tabs = useCartTabs();
  const { data: productSizes, isLoading: isLoadingSizes } = useProductSizes(
    item.uri
  );

  const isCartItemWithHooks = (
    item: CartItemMaybeHooked | GeneratedCartItem
  ): item is CartItemWithHooks => {
    return (item as CartItemWithHooks).increment !== undefined;
  };
  let isWithHooks = isCartItemWithHooks(item);
  if (!item?.name) {
    return null;
  }

  if (simple) {
    isWithHooks = false;
  }

  const handleSizeChange = (newSizeItemId: string) => {
    if (isWithHooks) {
      (item as CartItemWithHooks).changeSize(newSizeItemId);
    }
  };

  const showSizeSelector =
    isWithHooks &&
    !simple &&
    item.size !== "One Size" &&
    productSizes &&
    productSizes.length > 1;

  return (
    <div className={styles.item}>
      <CartImage item={item} />

      <div className={styles.details__container}>
        <div className={styles.left__column}>
          <Link href={getProductPageHref(item)}>
            <strong className={styles.item__name}>{item.name}</strong>
          </Link>

          <p>{"Color: " + item.variant}</p>

          {showSizeSelector ? (
            <CartSizeSelector
              currentSize={item.size}
              sizes={productSizes}
              onSizeChange={handleSizeChange}
              disabled={isLoadingSizes}
            />
          ) : (
            item.size !== "One Size" && <p>{"Size: " + item.size}</p>
          )}

          {isPreorder && <p>Pre-order</p>}
          {quantity && item.quantity && <p>{"Qty: " + item.quantity}</p>}
          {item?.giftbox && <p>With giftbox</p>}

          {isWithHooks && (
            <button
              onClick={() => {
                (item as CartItemWithHooks).moveToWishlist(() =>
                  tabs?.setTab("wishlist")
                );
              }}
              className={styles["move-button"]}
            >
              <span className={styles["move-button-span"]}>
                Move to wishlist
              </span>
            </button>
          )}
        </div>
        {price && (
          <div className={styles.right__column}>
            <CartItemPrice
              discountedPrice={item.discountedPrice}
              price={item.price}
            />

            <CartItemQuantity item={isWithHooks ? item : undefined} />

            <DeleteCartItem item={isWithHooks ? item : undefined} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartItem;
